import { Alert, defineComponent, inferProps, type ReactiveComponent } from "vue-utils"
import { type HomePage, type LessonPage, type SkillSetPage } from "../../../models"
import { css } from "vite-css-in-js"
import BootstrapButton from "../../../components/BootstrapButton"
import { computed, ref } from "vue"
import { usePopups } from "../../../composition/usePopups"

interface Props {
   home: HomePage
   setPage(page: LessonPage | SkillSetPage): void
   openFlashCards(): void
}

const pageStyles = css`
   height: 100%;
   display: flex;
   flex-direction: column;
   justify-content: center;
   background-color: #D8EFEE;

   h1 {
      margin-top: 4rem;
   }

   a{
      color: #2397F3;
   }

   p {
      padding: 0 2rem;
   }
`

const skillCards = css`
   display: flex;
   justify-content: center;
   gap: 1rem;
   max-height: 40vh;
   padding: 0 2rem;
   

   div.card {
      display: flex;
      width: 100%;
      text-align: center;
      flex-direction: column;
   }

   div.card:hover div.frame {
      background-color: #dddddd;
      transition: 0.2s;
   }

   div.frame {
      cursor: pointer;
      width: 100%;
      aspect-ratio: 5 / 3;
      background-color: #fff;
      border-radius: 999px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 1rem;
      box-sizing: border-box;
      overflow: hidden;  
      transition: 0.2s;
   }

   img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;       
      object-position: center;
      display: block;
   }

   p {
      text-align: center;
      margin: 0.2rem;
   }
`

const lessonButtons = css`
   display: flex;
   flex-direction: column;
   padding: 0 2rem;
   margin-top: 5rem;
   gap: 1rem;
   
   button {
      width:100%;
      background-color: #fff;
      border: none;
      border-radius: 16px;
      height: 40px;
      display: flex;
      justify-content: space-between;
      padding: 0 2rem;
      color: #00A89E;
      font-size: 16px;
      cursor: pointer;
      transition: 0.2s;
   }

   button:hover {
      background-color: #dddddd;
      transition: 0.2s;
   }
`

const installationPrompt = css`
   position: absolute;
   top: 1rem;
   left: 1rem;
   background-color: #fff;
   border: none;
   border-radius: 8px;
   display: flex;
   justify-content: space-between;
   padding: 8px;
   color: #00A89E;
   font-size: 16px;
   cursor: pointer;
   transition: 0.2s;

   &:hover {
      background-color: #dddddd;
      transition: 0.2s;
   }
`

interface InstallEvent extends Event {
   prompt(): void
}

const PWAHomePage: ReactiveComponent<Props> = (props) => {
   const installEvent = ref<InstallEvent|null>(null)
   const popups = usePopups()

   function installOnIosPopup() {
      void popups.showAlertPopup(() => (
         <Alert title="Installation">To install this app, tap the share button at the bottom of your screen and tap 'Add to Home Screen'.</Alert>
      ))
   }
   
   window.addEventListener('beforeinstallprompt', e => {
      e.preventDefault();
      installEvent.value = window.matchMedia('(display-mode: standalone)').matches ? null : e as InstallEvent;
   });

   window.addEventListener('appinstalled', () => {
      installEvent.value = null
   });

   const isActualIosSafari = computed(() => {
      const ua = window.navigator.userAgent;
      // 1. Check if the device is iOS (iPhone/iPad/iPod)
      const isIOS = /iPad|iPhone|iPod/.test(ua);
      // 2. Check if the string contains "Safari" 
      const containsSafari = /Safari/.test(ua);
      // 3. Rule out alternative iOS browsers (Chrome uses CriOS, Firefox uses FxiOS)
      const isChromiumIOS = /CriOS/.test(ua);
      const isFirefoxIOS = /FxiOS/.test(ua);
      const isEdgeIOS = /EdgiOS/.test(ua);
      
      // It is true Safari only if it is iOS, has the Safari token, and isn't a competitor browser
      return isIOS && containsSafari && !isChromiumIOS && !isFirefoxIOS && !isEdgeIOS;
   })

	return () => (
      <div class={pageStyles}>
         {(installEvent.value && !isActualIosSafari.value) ? (
            <BootstrapButton class={installationPrompt} color="decider" onClick={() => installEvent.value!.prompt()}><i class="fas fa-arrow-down" /> Install App</BootstrapButton>
         ) : (
            <BootstrapButton class={installationPrompt} color="decider" onClick={() => installOnIosPopup()}><i class="fas fa-arrow-down" /> Install App</BootstrapButton>
         )}
         <h1 v-html={props.home.properties.heading?.markup} />
         <div class={skillCards}>
            {props.home.properties.skillSets.map((s) => (
               <div key={s.id} class="card">
                  <div class="frame" onClick={() => props.setPage(s)}>
                     {s.properties.image && (
                        <img src={`https://pwa-admin.thedecider.org.uk${s.properties.image[0]?.url}`} alt={s.properties.image[0]?.name}/>
                     )}
                     <p>{s.name}</p>
                  </div>
               </div>
            ))}
         </div>
         <div class={lessonButtons}>
            {props.home.properties.lessons.map((l) => (
               <BootstrapButton key={l.id} color="decider" onClick={() => props.setPage(l)}>
                  {l.name}
                  <i class="fas fa-arrow-right" />
               </BootstrapButton>
            ))}
            <BootstrapButton color="decider" onClick={() => props.openFlashCards()}>
               Mini Game
               <i class="fas fa-arrow-right" />
            </BootstrapButton>
         </div>
         <p>All the information within the app, has been provided by The Decider. <a href="https://www.thedecider.org.uk/" target="_blank">(Click here to view website)</a></p>
      </div>
	)
}
export default defineComponent(PWAHomePage, inferProps<Props>())